import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Compass, Building2, Stethoscope, Sparkles } from 'lucide-react';
import { PublicNavbar } from '../components/brand/PublicNavbar';
import { Footer } from '../components/brand/Footer';
import { Seo } from '../components/seo/Seo';

/**
 * ArchivedFeaturePage — graceful placeholder for any V1-archived feature.
 *
 * Rendered by <FeatureGate> when isFeatureLive(featureId) is false. The
 * route itself still exists so external links + prerendered HTML don't
 * break; visitors get a forward-looking "here's what we're focused on
 * today" surface instead of a "we're paused" tombstone.
 *
 * Iter128 (Feb 2026): rewrote copy to remove any use of the word
 * "paused" — that word reads as "the company is paused" to first-time
 * visitors and undermines conversion. The strategic focus is preserved
 * (route stays gated), but the surface now routes visitors to the pages
 * that DO convert (providers, facilities, clinicians, pricing) instead
 * of dead-ending them.
 *
 * When the feature resurrects (its entry moves from
 * FEATURE_LIFECYCLE.archived to v1/v2/v3 in caraloom_v2_ia.js), this
 * placeholder disappears with no additional engineering — the underlying
 * page renders again automatically.
 */
const ArchivedFeaturePage = ({ featureId }) => {
  return (
    <div className="min-h-screen bg-cn-bg text-cn-text">
      <Seo
        title="What's live today — CaraLoom"
        description="CaraLoom is the AI Operating System for post-acute care providers, the facilities that refer to them, and the clinicians who power them. Here's what's live today."
        path=""
        noindex
      />
      <PublicNavbar />
      <section className="cn-container py-24 md:py-32" data-testid="archived-feature-page">
        <div className="max-w-3xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-cn-primary/10 border border-cn-primary/20 mb-6">
            <Sparkles className="w-3.5 h-3.5 text-cn-primary" aria-hidden="true" />
            <span className="text-xs font-semibold tracking-wide uppercase text-cn-primary">
              On the roadmap
            </span>
          </div>
          <h1 className="cn-display text-4xl sm:text-5xl text-cn-text mb-5">
            We&apos;re focused on the care ecosystem first.
          </h1>
          <p className="text-lg text-cn-text-muted leading-relaxed mb-10 max-w-2xl mx-auto">
            CaraLoom is the AI Operating System for post-acute care providers,
            the facilities that refer to them, and the independent clinicians
            who power them. This surface is on the roadmap — meanwhile, here&apos;s
            what&apos;s live today.
          </p>

          <div className="grid sm:grid-cols-3 gap-3 mb-10 text-left">
            <Link
              to="/for-discharge-planners"
              className="group p-5 rounded-2xl border border-cn-border bg-cn-surface hover:border-cn-primary/60 hover:-translate-y-0.5 transition"
              data-testid="archived-redirect-facilities"
            >
              <Building2 className="w-5 h-5 text-cn-primary mb-3" />
              <div className="font-semibold text-cn-text mb-1">Hospitals & facilities</div>
              <p className="text-sm text-cn-text-muted leading-snug">
                Discharge in minutes, not days.
              </p>
              <span className="inline-flex items-center gap-1 mt-3 text-xs font-semibold text-cn-primary group-hover:gap-2 transition-all">
                Explore <ArrowRight className="w-3 h-3" />
              </span>
            </Link>
            <Link
              to="/suites/operations"
              className="group p-5 rounded-2xl border border-cn-border bg-cn-surface hover:border-cn-primary/60 hover:-translate-y-0.5 transition"
              data-testid="archived-redirect-providers"
            >
              <Compass className="w-5 h-5 text-cn-primary mb-3" />
              <div className="font-semibold text-cn-text mb-1">Home health & agencies</div>
              <p className="text-sm text-cn-text-muted leading-snug">
                Operate one command center for census, comp, and compliance.
              </p>
              <span className="inline-flex items-center gap-1 mt-3 text-xs font-semibold text-cn-primary group-hover:gap-2 transition-all">
                Explore <ArrowRight className="w-3 h-3" />
              </span>
            </Link>
            <Link
              to="/for-clinicians"
              className="group p-5 rounded-2xl border border-cn-border bg-cn-surface hover:border-cn-primary/60 hover:-translate-y-0.5 transition"
              data-testid="archived-redirect-clinicians"
            >
              <Stethoscope className="w-5 h-5 text-cn-primary mb-3" />
              <div className="font-semibold text-cn-text mb-1">Clinicians</div>
              <p className="text-sm text-cn-text-muted leading-snug">
                Independent, well-supported, well-compensated.
              </p>
              <span className="inline-flex items-center gap-1 mt-3 text-xs font-semibold text-cn-primary group-hover:gap-2 transition-all">
                Explore <ArrowRight className="w-3 h-3" />
              </span>
            </Link>
          </div>

          <div className="flex flex-wrap items-center justify-center gap-3">
            <Link
              to="/"
              className="cn-btn cn-btn-primary"
              data-testid="archived-back-home"
            >
              Back to CaraLoom <ArrowRight className="w-4 h-4" />
            </Link>
            <Link
              to="/pricing"
              className="cn-btn cn-btn-secondary"
              data-testid="archived-see-pricing"
            >
              See the platform
            </Link>
          </div>
          {/*
            Internal note (HTML-comment, never shown to users):
            featureId={featureId} — see /app/memory/CARALOOM_V2_ARCHIVE_PLAN.md
            for the resurrection criterion. When the criterion is met, move
            this feature's entry from FEATURE_LIFECYCLE.archived to the
            relevant tier in /app/frontend/src/lib/caraloom_v2_ia.js — the
            placeholder disappears automatically; no other code change.
          */}
          {process.env.NODE_ENV !== 'production' && featureId && (
            <p className="mt-12 text-xs text-cn-text-muted/60 font-mono">
              dev: archived feature <code>{featureId}</code>
            </p>
          )}
        </div>
      </section>
      <Footer />
    </div>
  );
};

export default ArchivedFeaturePage;
